import {Controller} from "@hotwired/stimulus"
import {Turbo} from "@hotwired/turbo-rails"

// Connects to data-controller="poll"
//
// A defense-in-depth fallback for live Turbo Stream updates. The primary
// update path is the cable broadcast (ListingImport#broadcast_status_change);
// this controller periodically re-fetches a turbo_stream fragment so the page
// still resolves if the cable subscription is delayed or a broadcast is missed
// (slow WebSocket handshake, dropped socket). The server renders the SAME
// target/partial for both paths, so they converge idempotently.
//
// Attach it to the element the fragment replaces, and only while the work is in
// progress — once the server renders a terminal state the replacement no longer
// carries this controller, so disconnect() clears the timer and polling stops.
//
//   data: { controller: "poll",
//           poll_url_value: some_path(record, format: :turbo_stream),
//           poll_interval_value: 4000 }
export default class extends Controller {
    static values = {
        url: String,
        interval: {type: Number, default: 4000},
        maxAttempts: {type: Number, default: 75}
    }

    connect() {
        this.attempts = 0
        this.inFlight = false
        this.timer = setInterval(() => this.poll(), this.intervalValue)
    }

    disconnect() {
        if (this.timer) clearInterval(this.timer)
    }

    async poll() {
        if (this.inFlight) return

        this.attempts += 1
        if (this.attempts > this.maxAttemptsValue) {
            this.disconnect()
            return
        }

        this.inFlight = true
        try {
            const response = await fetch(this.urlValue, {
                headers: {"Accept": "text/vnd.turbo-stream.html"},
                credentials: "same-origin"
            })
            if (response.ok) {
                Turbo.renderStreamMessage(await response.text())
            }
        } catch (error) {
            // Swallow transient network errors — the next tick retries, and the
            // cable broadcast may resolve the page in the meantime.
            console.error("poll: fetch failed", error)
        } finally {
            this.inFlight = false
        }
    }
}
